import { Link } from 'react-router-dom';
import { SectionCard } from '@/components/ui/SectionCard';
import { StatusPill } from '@/components/ui/StatusPill';
import { useI18n } from '@/hooks/useI18n';
import type { DoctoralCandidateItem, WorkRequestItem } from '../types';

interface ModuleDataViewProps {
  moduleKey: string;
  tab: string;
  data: Record<string, unknown>;
  basePath: string;
}

const statusTone = (status: string): 'info' | 'warning' | 'success' | 'neutral' => {
  if (status.includes('PROGRESS') || status === 'ACTIVE') return 'info';
  if (
    status.includes('NEEDS') ||
    status.includes('PENDING') ||
    status === 'AT_RISK' ||
    status === 'ON_REVIEW' ||
    status === 'REJECTED' ||
    status === 'CANCELLED'
  )
    return 'warning';
  if (
    status === 'COMPLETED' ||
    status === 'CONFIRMED' ||
    status === 'READY' ||
    status === 'APPROVED' ||
    status === 'PUBLISHED'
  )
    return 'success';
  return 'neutral';
};

export const ModuleDataView = ({ moduleKey, tab, data, basePath }: ModuleDataViewProps) => {
  const { t } = useI18n();

  if (data.dossier && typeof data.dossier === 'object') {
    const dossier = data.dossier as {
      candidate?: Record<string, unknown>;
      sections?: Array<Record<string, unknown>>;
    };
    const candidate = dossier.candidate ?? {};
    const sections = Array.isArray(dossier.sections) ? dossier.sections : [];
    return (
      <div className="full-grid">
        <SectionCard title={String(candidate.full_name ?? 'Досье докторанта')} span={12}>
          <ul className="info-list">
            <li>
              <strong>Academia ID</strong>
              <span>{String(candidate.academia_id ?? '—')}</span>
            </li>
            <li>
              <strong>Программа</strong>
              <span>{String(candidate.program ?? '—')}</span>
            </li>
            <li>
              <strong>Курс</strong>
              <span>{String(candidate.year ?? '—')}</span>
            </li>
            <li>
              <strong>Руководитель</strong>
              <span>{String(candidate.supervisor || '—')}</span>
            </li>
            <li>
              <strong>Тема</strong>
              <span>{String(candidate.topic || '—')}</span>
            </li>
            <li>
              <strong>ИИН / Место работы</strong>
              <span>
                {String(candidate.iin || '—')} · {String(candidate.workplace || '—')}
              </span>
            </li>
          </ul>
        </SectionCard>
        {sections.map((section) => (
          <SectionCard key={String(section.section)} title={String(section.title)} span={6}>
            <p>{String(section.content || '—')}</p>
            <StatusPill tone={statusTone(String(section.review_state ?? 'neutral'))}>
              {String(section.review_state ?? '—')}
            </StatusPill>
          </SectionCard>
        ))}
        <div>
          <Link to={`${basePath}/doctoral_process`} className="workspace-back">
            ← К списку докторантов
          </Link>
        </div>
      </div>
    );
  }

  if (moduleKey === 'coverage' && Array.isArray(data.directions)) {
    return (
      <div className="coverage-grid">
        {(data.directions as Array<Record<string, unknown>>).map((direction) => (
          <SectionCard key={String(direction.code)} title={String((direction.label as Record<string, string>)?.ru ?? direction.code)} span={6}>
            <ul className="coverage-list">
              {((direction.services as Array<Record<string, unknown>>) ?? []).slice(0, 6).map((service) => (
                <li key={String(service.id)}>
                  <strong>{String(service.service_code)}</strong>
                  <span>{String(service.service_label)}</span>
                </li>
              ))}
            </ul>
          </SectionCard>
        ))}
      </div>
    );
  }

  if (Array.isArray(data.toolbox)) {
    return (
      <div className="action-grid">
        {(data.toolbox as Array<Record<string, unknown>>).map((item) => (
          <div key={String(item.id)} className="action-card">
            <strong>{String(item.label)}</strong>
            <small>{String(item.count)}</small>
          </div>
        ))}
      </div>
    );
  }

  if (Array.isArray(data.templates)) {
    return (
      <table className="data-table">
        <thead>
          <tr>
            <th>{t('workspace.col.title')}</th>
            <th>{t('workspace.col.kind')}</th>
            <th>{t('workspace.col.size')}</th>
          </tr>
        </thead>
        <tbody>
          {(data.templates as Array<Record<string, unknown>>).map((item) => (
            <tr key={String(item.title)}>
              <td>{String(item.title)}</td>
              <td>{String(item.kind)}</td>
              <td>{String(item.size)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  if (moduleKey === 'monitoring' && tab === 'overview') {
    return (
      <div className="full-grid">
        <SectionCard title={t('workspace.monitoring.total')} span={4}>
          <p className="workspace-stat">{String(data.total ?? 0)}</p>
        </SectionCard>
        <SectionCard title={t('workspace.monitoring.defended')} span={4}>
          <p className="workspace-stat">{String(data.defended ?? 0)}</p>
        </SectionCard>
        <SectionCard title={t('workspace.monitoring.years')} span={4}>
          <dl>{Object.entries((data.by_year ?? {}) as Record<string, unknown>).map(([year, count]) => (
            <div key={year}><dt>{year}</dt><dd>{String(count)}</dd></div>
          ))}</dl>
        </SectionCard>
      </div>
    );
  }

  if (moduleKey === 'points' && tab === 'overview') {
    return (
      <div className="full-grid">
        <SectionCard title={t('workspace.points.rules')} span={6}>
          <p className="workspace-stat">{String(data.rules_count ?? 0)}</p>
        </SectionCard>
        <SectionCard title={t('workspace.points.ledger')} span={6}>
          <p className="workspace-stat">{String(data.ledger_count ?? 0)}</p>
        </SectionCard>
      </div>
    );
  }

  if (moduleKey === 'profile' && data.profile) {
    const profile = data.profile as Record<string, unknown>;
    return (
      <div className="full-grid">
        <SectionCard title={t('workspace.researcher.profile.ids')} span={6}>
          <ul className="info-list">
            {Boolean(profile.orcid) && <li><strong>ORCID</strong><span>{String(profile.orcid)}</span></li>}
            {Boolean(profile.scopus_id) && <li><strong>Scopus</strong><span>{String(profile.scopus_id)}</span></li>}
            {Boolean(profile.wos_id) && <li><strong>WoS</strong><span>{String(profile.wos_id)}</span></li>}
            {Boolean(profile.scholar_url) && (
              <li>
                <strong>Scholar</strong>
                <a href={String(profile.scholar_url)} target="_blank" rel="noreferrer">
                  {String(profile.scholar_url)}
                </a>
              </li>
            )}
          </ul>
        </SectionCard>
        {Boolean(profile.bio) && (
          <SectionCard title={t('workspace.researcher.profile.bio')} span={6}>
            <p>{String(profile.bio)}</p>
          </SectionCard>
        )}
        {Boolean(profile.interests) && (
          <SectionCard title={t('workspace.researcher.profile.interests')} span={12}>
            <p>{String(profile.interests)}</p>
          </SectionCard>
        )}
      </div>
    );
  }

  if (moduleKey === 'profile' && !data.profile) {
    return <p className="workspace-empty">{t('common.notFound')}</p>;
  }

  if (!Array.isArray(data.items)) {
    return <p className="workspace-empty">{t('common.notFound')}</p>;
  }

  if (moduleKey === 'requests' || (moduleKey === 'orders' && tab === 'author')) {
    const items = data.items as WorkRequestItem[];
    return (
      <table className="data-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>{t('workspace.col.requester')}</th>
            <th>{t('workspace.col.service')}</th>
            <th>{t('workspace.col.status')}</th>
            <th>{t('workspace.col.assignee')}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id}>
              <td>{item.reference_code ?? item.id}</td>
              <td>{item.requester_name}</td>
              <td>{item.service_name ?? (item as unknown as Record<string, string>).title}</td>
              <td>
                <StatusPill tone={statusTone(item.status)}>{item.status}</StatusPill>
              </td>
              <td>{item.assignee ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  if (moduleKey === 'doctoral_process' || moduleKey === 'monitoring') {
    const items = data.items as DoctoralCandidateItem[];
    return (
      <table className="data-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>{t('workspace.col.name')}</th>
            <th>{t('workspace.col.program')}</th>
            <th>{t('workspace.col.year')}</th>
            <th>{t('workspace.col.rating')}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id}>
              <td>{item.academia_id}</td>
              <td>
                <Link to={`${basePath}/doctoral_process?candidate=${item.id}`}>{item.full_name}</Link>
              </td>
              <td>{item.program}</td>
              <td>{item.year}</td>
              <td>{item.points_rating}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  if (moduleKey === 'supervision' && tab === 'topics') {
    const items = data.items as Array<Record<string, unknown>>;
    return (
      <table className="data-table">
        <thead>
          <tr>
            <th>{t('workspace.col.name')}</th>
            <th>{t('workspace.col.program')}</th>
            <th>Тема</th>
            <th>{t('workspace.col.status')}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={String(item.id)}>
              <td>{String(item.student_name)}</td>
              <td>{String(item.program)}</td>
              <td>{String(item.topic)}</td>
              <td>
                <StatusPill tone={statusTone(String(item.status ?? 'neutral'))}>
                  {String(item.status ?? '—')}
                </StatusPill>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  if (moduleKey === 'supervision' && (tab === 'students' || tab === 'all' || tab === 'bachelor' || tab === 'master' || tab === 'doctoral')) {
    const items = data.items as Array<Record<string, unknown>>;
    return (
      <table className="data-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>{t('workspace.col.name')}</th>
            <th>Уровень / Программа</th>
            <th>{t('workspace.col.status')}</th>
            <th>%</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={String(item.id)}>
              <td>{String(item.academia_id)}</td>
              <td>{String(item.full_name)}</td>
              <td>{String(item.education_level ?? '')} {String(item.program)}</td>
              <td>
                <StatusPill tone={statusTone(String(item.review_status ?? 'neutral'))}>
                  {String(item.review_status ?? '—')}
                </StatusPill>
              </td>
              <td>{String(item.progress_pct ?? 0)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  if (moduleKey === 'moderation') {
    const items = data.items as Array<Record<string, unknown>>;
    return (
      <table className="data-table">
        <thead>
          <tr>
            <th>Код заявки</th>
            <th>Студент</th>
            <th>Услуга</th>
            <th>{t('workspace.col.status')}</th>
            <th>Решение</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={String(item.id)}>
              <td>{String(item.request_code)}</td>
              <td>{String(item.student_name)}</td>
              <td>{String(item.service_name)}</td>
              <td>
                <StatusPill tone={statusTone(String(item.status ?? 'neutral'))}>
                  {String(item.status ?? '—')}
                </StatusPill>
              </td>
              <td>{String(item.decision_note ?? '—')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  if (moduleKey === 'personal_requests') {
    const items = data.items as Array<Record<string, unknown>>;
    return (
      <table className="data-table">
        <thead>
          <tr>
            <th>Код</th>
            <th>{t('workspace.col.kind')}</th>
            <th>{t('workspace.col.title')}</th>
            <th>{t('workspace.col.status')}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={String(item.id)}>
              <td>{String(item.reference_code)}</td>
              <td>{String(item.kind)}</td>
              <td>{String(item.title)}</td>
              <td>
                <StatusPill tone={statusTone(String(item.status ?? 'neutral'))}>
                  {String(item.status ?? '—')}
                </StatusPill>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  if (moduleKey === 'publications') {
    const items = data.items as Array<Record<string, unknown>>;
    return (
      <table className="data-table">
        <thead>
          <tr>
            <th>{t('workspace.col.title')}</th>
            <th>Журнал</th>
            <th>Год</th>
            <th>{t('workspace.col.status')}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={String(item.id)}>
              <td>{String(item.title)}</td>
              <td>{String(item.journal ?? '—')}</td>
              <td>{String(item.year)}</td>
              <td>
                <StatusPill tone={statusTone(String(item.status ?? 'neutral'))}>
                  {String(item.status)}
                </StatusPill>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  if (moduleKey === 'grants') {
    const items = data.items as Array<Record<string, unknown>>;
    return (
      <table className="data-table">
        <thead>
          <tr>
            <th>{t('workspace.col.title')}</th>
            <th>Конкурс / Организация</th>
            <th>Роль</th>
            <th>{t('workspace.col.status')}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={String(item.id)}>
              <td>{String(item.title)}</td>
              <td>{String(item.competition ?? '')} · {String(item.organization ?? '')}</td>
              <td>{String(item.role ?? '—')}</td>
              <td>
                <StatusPill tone={statusTone(String(item.status ?? 'neutral'))}>
                  {String(item.status)}
                </StatusPill>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  if (moduleKey === 'manuscripts') {
    const items = data.items as Array<Record<string, unknown>>;
    return (
      <table className="data-table">
        <thead>
          <tr>
            <th>{t('workspace.col.title')}</th>
            <th>Целевой журнал</th>
            <th>Версия</th>
            <th>{t('workspace.col.status')}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={String(item.id)}>
              <td>{String(item.title)}</td>
              <td>{String(item.target_journal ?? '—')}</td>
              <td>v{String(item.current_version ?? 1)}</td>
              <td>
                <StatusPill tone={statusTone(String(item.stage ?? 'neutral'))}>
                  {String(item.stage ?? '—')}
                </StatusPill>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  if (moduleKey === 'coauthors') {
    const items = data.items as Array<Record<string, unknown>>;
    return (
      <table className="data-table">
        <thead>
          <tr>
            <th>{t('workspace.col.name')}</th>
            <th>Academia ID</th>
            <th>Совместных работ</th>
            <th>Контекст</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={String(item.id)}>
              <td>{String(item.name)}</td>
              <td>{String(item.academia_id ?? '—')}</td>
              <td>{String(item.shared_count ?? 0)}</td>
              <td>{String(item.context ?? '—')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  if (moduleKey === 'achievements') {
    const items = data.items as Array<Record<string, unknown>>;
    return (
      <table className="data-table">
        <thead>
          <tr>
            <th>{t('workspace.col.title')}</th>
            <th>{t('workspace.col.kind')}</th>
            <th>Проверка</th>
            <th>Видимость</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={String(item.id)}>
              <td>{String(item.title)}</td>
              <td>{String(item.kind)}</td>
              <td>
                <StatusPill tone={item.is_verified ? 'success' : 'warning'}>
                  {item.is_verified ? 'Подтверждено' : 'На проверке'}
                </StatusPill>
              </td>
              <td>{item.is_public ? 'Публично' : 'Приватно'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  if (moduleKey === 'opportunities') {
    const items = data.items as Array<Record<string, unknown>>;
    return (
      <table className="data-table">
        <thead>
          <tr>
            <th>{t('workspace.col.title')}</th>
            <th>{t('workspace.col.kind')}</th>
            <th>Дедлайн</th>
            <th>Relevance</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={String(item.id)}>
              <td>{String(item.title)}</td>
              <td>{String(item.kind)}</td>
              <td>{String(item.deadline_label ?? '—')}</td>
              <td>{String(item.relevance ?? '—')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  const items = data.items as Array<Record<string, unknown>>;
  return (
    <table className="data-table">
      <thead>
        <tr>
          <th>{t('workspace.col.title')}</th>
          <th>{t('workspace.col.status')}</th>
          <th>{t('workspace.col.details')}</th>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <tr key={String(item.id)}>
            <td>
              {String(
                item.title ??
                  item.subject ??
                  item.full_name ??
                  item.student_name ??
                  item.correspondent_name ??
                  item.author_name ??
                  item.candidate_name ??
                  item.name ??
                  '—',
              )}
            </td>
            <td>
              <StatusPill tone={statusTone(String(item.status ?? item.readiness ?? item.plan_status ?? item.defence_status ?? 'neutral'))}>
                {String(item.status ?? item.readiness ?? item.plan_status ?? item.defence_status ?? item.kind ?? '—')}
              </StatusPill>
            </td>
            <td>
              {String(
                item.preview ??
                  item.journal ??
                  item.partner_name ??
                  item.email ??
                  item.semester ??
                  item.points ??
                  item.organization ??
                  item.context ??
                  item.date ??
                  item.version ??
                  '—',
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};
